import { DestroyRef } from '@angular/core';
import type { Observable } from 'rxjs';

import type { GridBucketScope, GridRowsPage, GridSortField } from '../contracts/grid-data-source.interface';
import type {
  GridBackendAttributeFilter,
  GridBackendAttributeFilterType
} from '../d-data-grid/column-filter.types';
import type { GridOrchestrationDataAccess } from './grid-orchestration-data-access';
import type { BucketLoadingHost } from './grid-orchestration-bucket-loading';
import type { GroupDiscoveryHost } from './grid-orchestration-group-discovery';
import type { GridPaginationHost } from './grid-orchestration-pagination';
import type { GroupPaginationState, GroupingCapWarning } from './grid-orchestration.types';

/**
 * Shared state accessors and callbacks used to build host adapters for extracted
 * grid orchestration modules. Implemented by {@link GridOrchestrationService}.
 */
export interface GridOrchestrationHostContext {
  readonly elementsProvider: GridOrchestrationDataAccess;
  readonly destroyRef: DestroyRef;
  getQueryGeneration: () => number;
  getLastGridWhere: () => Record<string, unknown>;
  getGroupPagination: () => Map<string, GroupPaginationState> | null;
  setGroupPagination: (pagination: Map<string, GroupPaginationState> | null) => void;
  updateGroupPagination: (
    updater: (current: Map<string, GroupPaginationState>) => Map<string, GroupPaginationState>
  ) => void;
  getSubGroupCounts: () => Record<string, number>;
  setSubGroupCounts: (counts: Record<string, number>) => void;
  getGridGroupByFields: () => Array<string>;
  getGridEndCursor: () => string | null;
  setGridEndCursor: (cursor: string | null) => void;
  getGridLoadingMore: () => boolean;
  beginGridLoadingMore: () => void;
  endGridLoadingMore: () => void;
  setGridHasMore: (hasMore: boolean) => void;
  setElementsError: (message: string | null) => void;
  getElements: () => GridRowsPage | null;
  setElements: (response: GridRowsPage) => void;
  addToBatchLoadQueue: (path: string) => void;
  takeBatchLoadQueueSnapshot: () => Array<string>;
  scheduleBatchFlush: (flush: () => void) => void;
  scheduleBatchFallbackLoad: (path: string, delayMs: number, queryGeneration: number) => void;
  mergeAttributeFilters: (
    extra?: Array<GridBackendAttributeFilter>
  ) => Array<GridBackendAttributeFilter> | undefined;
  remappedFields: () => ReadonlySet<string>;
  resolveColumnType: (field: string) => GridBackendAttributeFilterType | undefined;
  buildNullOperatorEntry: (name: string, operator: 'IS_NULL' | 'IS_NOT_NULL') => GridBackendAttributeFilter;
  getPrimaryGroupField: () => string | null;
  getDiscriminatorField: () => string;
  addHighCardinalityField: (field: string) => void;
  reportGroupingCapExceeded: (warning: GroupingCapWarning) => void;
  fetchGridPage: (where: Record<string, unknown>, after?: string) => Observable<GridRowsPage>;
  fetchNullBucketViaClientFilter: (
    path: string,
    bucketWhere: Record<string, unknown>,
    scanAfter: string | undefined,
    pageSize: number,
    expectedTotal: number,
    alreadyLoaded: number
  ) => Observable<GridRowsPage>;
  buildWhereFromPath: (path: string) => Record<string, unknown>;
  buildBucketInputFromPath: (path: string) => GridBucketScope;
  buildBatchBucketRequestOptions: (buckets: Array<GridBucketScope>) => {
    buckets: Array<GridBucketScope>;
    firstPerBucket: number;
    sharedWhere: Record<string, unknown>;
    attributeFilter?: Array<GridBackendAttributeFilter>;
    sort?: Array<GridSortField>;
  };
  isNullBucketPath: (path: string) => boolean;
  isEmptyElementsResponse: (response: GridRowsPage) => boolean;
  logEmptyNodeMismatch: (bucketKey: string, response: GridRowsPage) => void;
  applyBucketFetchResult: (
    bucketKey: string,
    response: GridRowsPage,
    options?: { clientNullFilter?: boolean; cursorSource?: 'batch' | 'elements' }
  ) => void;
}

/** Builds the host adapter passed to extracted group-discovery helpers. */
export function buildGroupDiscoveryHost(ctx: GridOrchestrationHostContext): GroupDiscoveryHost {
  return {
    elementsProvider: ctx.elementsProvider,
    getQueryGeneration: ctx.getQueryGeneration,
    mergeAttributeFilters: ctx.mergeAttributeFilters,
    remappedFields: ctx.remappedFields,
    resolveColumnType: ctx.resolveColumnType,
    buildNullOperatorEntry: ctx.buildNullOperatorEntry,
    getPrimaryGroupField: ctx.getPrimaryGroupField,
    getDiscriminatorField: ctx.getDiscriminatorField,
    getGroupPagination: ctx.getGroupPagination,
    setGroupPagination: (pagination: Map<string, GroupPaginationState>): void => {
      ctx.setGroupPagination(pagination);
    },
    getSubGroupCounts: ctx.getSubGroupCounts,
    setSubGroupCounts: ctx.setSubGroupCounts,
    addHighCardinalityField: ctx.addHighCardinalityField,
    getElements: ctx.getElements,
    setElements: ctx.setElements,
    reportGroupingCapExceeded: ctx.reportGroupingCapExceeded
  };
}

/** Builds the host adapter passed to extracted bucket-loading helpers. */
export function buildBucketLoadingHost(ctx: GridOrchestrationHostContext): BucketLoadingHost {
  return {
    elementsProvider: ctx.elementsProvider,
    getQueryGeneration: ctx.getQueryGeneration,
    fetchGridPage: (where: Record<string, unknown>): Observable<GridRowsPage> => ctx.fetchGridPage(where),
    buildBucketInputFromPath: ctx.buildBucketInputFromPath,
    buildWhereFromPath: ctx.buildWhereFromPath,
    buildBatchBucketRequestOptions: ctx.buildBatchBucketRequestOptions,
    logEmptyNodeMismatch: ctx.logEmptyNodeMismatch,
    applyBucketFetchResult: ctx.applyBucketFetchResult,
    getGroupPagination: ctx.getGroupPagination,
    setGroupPagination: (pagination: Map<string, GroupPaginationState>): void => {
      ctx.setGroupPagination(pagination);
    }
  };
}

/** Builds the host adapter passed to extracted pagination helpers. */
export function buildPaginationHost(ctx: GridOrchestrationHostContext): GridPaginationHost {
  return {
    elementsProvider: ctx.elementsProvider,
    destroyRef: ctx.destroyRef,
    getQueryGeneration: ctx.getQueryGeneration,
    getGridEndCursor: ctx.getGridEndCursor,
    setGridEndCursor: ctx.setGridEndCursor,
    getGridLoadingMore: ctx.getGridLoadingMore,
    beginGridLoadingMore: ctx.beginGridLoadingMore,
    endGridLoadingMore: ctx.endGridLoadingMore,
    scheduleBatchFallbackLoad: ctx.scheduleBatchFallbackLoad,
    setGridHasMore: ctx.setGridHasMore,
    setElementsError: ctx.setElementsError,
    getElements: ctx.getElements,
    setElements: ctx.setElements,
    getLastGridWhere: ctx.getLastGridWhere,
    getGroupPagination: ctx.getGroupPagination,
    setGroupPagination: ctx.setGroupPagination,
    updateGroupPagination: ctx.updateGroupPagination,
    getGridGroupByFields: ctx.getGridGroupByFields,
    getSubGroupCounts: ctx.getSubGroupCounts,
    setSubGroupCounts: ctx.setSubGroupCounts,
    addToBatchLoadQueue: ctx.addToBatchLoadQueue,
    takeBatchLoadQueueSnapshot: ctx.takeBatchLoadQueueSnapshot,
    scheduleBatchFlush: (flush: () => void): void => {
      ctx.scheduleBatchFlush(flush);
    },
    fetchGridPage: ctx.fetchGridPage,
    fetchNullBucketViaClientFilter: ctx.fetchNullBucketViaClientFilter,
    buildWhereFromPath: ctx.buildWhereFromPath,
    buildBucketInputFromPath: ctx.buildBucketInputFromPath,
    buildBatchBucketRequestOptions: ctx.buildBatchBucketRequestOptions,
    isNullBucketPath: ctx.isNullBucketPath,
    isEmptyElementsResponse: ctx.isEmptyElementsResponse,
    logEmptyNodeMismatch: ctx.logEmptyNodeMismatch
  };
}
